import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import SurveyDropdown from './survey-components/dropdown';
import SurveyTextField from './survey-components/textfield';
import SurveyGrade from './survey-components/grade';
import SurveyRadio from './survey-components/radio';
import { Scrollbars } from 'react-custom-scrollbars';
import SurveyButton from './survey-components/button';
import SurveyDisclaimer from './survey-components/disclaimer';
import { SECONDARY_COLOR } from '../../color-constants';

const SurveyBackground = styled.div`
  position: fixed;
  justify-content: flex-end;
  align-items: center;
  display: flex;
  background-color: rgba(51,51,51,0.5);
  top: 0px; right: 0px; bottom: 0px; left: 0px;
  z-index: 450;
`;

const SurveyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 85%;
  max-height: 600px;
  max-width: 430px;
  min-width: 430px;
  color: #000000;

  @media (max-width: 430px) {
    min-width: 100%;
    max-width: 100%;
  }
`;

const SurveyHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${props => props.color || `${SECONDARY_COLOR}`};
  color: ${props => props.textColor || 'white'};
  padding: 10px;
`;

const AppTitle = styled.span`
  display: block;
  font-weight: 400;
  font-size: 16px;
`;

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  margin-right: 10px;
`;

const FinishedIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 58px;
  height: 58px;
  color: ${props => props.color || `${SECONDARY_COLOR}`};
`;

const CloseIconWrapper = styled.div`
  margin-left: 10px;
  cursor: pointer;
`;

const SurveyContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0px;
  padding: 20px 0px 20px 20px;
  background-color: white;
  overflow-x: hidden;
  overflow-y: auto;
`;

const SurveyTitle = styled.span`
  display: block;
  font-weight: 700;
  font-size: 16px;
`;

const ScrollablePadding = styled.div`
  padding-right: 20px;
  padding-bottom: 10px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex: 1 1 0px;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: 10px;
  padding-right: 20px;
`;

const ButtonWrapper = styled.div`
  margin-left: 10px;
`;

const QuestionTitle = styled.span`
  display: block;
  font-weight: 400;
  font-size: 14px;
  margin-top: 20px;
  margin-bottom: 20px;
  color: #000000;
`;

const OpenSurveyButton = styled.div`
  position: absolute;
  top: 250px;
  display: flex;
  align-items: center;
  right: 0px;
  background-color: ${props => props.color || `${SECONDARY_COLOR}`};
  color: ${props => props.textColor || 'white'};
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  transition: transform 250ms;
  transform: translateX(calc(100% - 36px));
  cursor: pointer;
  ${props => props.forceShowing && `transform: none;`}
  :hover {
    transform: none;
  }

  ${props => props.fullScreenEnabled && `
    position: fixed;
    bottom: 50%;
    z-index: 200;
    top: unset;
  `}

  @media (max-width: 800px) {
    position: fixed;
    bottom: 50%;
    z-index: 200;
    top: unset;
  }

  @media (min-width: 800px) {
    ${props => props.showDisclaimer && `
      z-index: 376;
      transform: none;
  `}
  }

`;

const ButtonElements = styled.div`
  display: flex;
  align-items: center;
  padding: 5px 10px 5px 5px;

  @media (min-width: 800px) {
    ${props => props.showDisclaimer ? `pointer-events: none;` : ``}
  }
`;

const OpenSurveyLabel = styled.span``;

const DoneWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0px;
  justify-content: center;
  align-items: center;
  padding-right: 20px;
`;

const DoneTitle = styled.span`
  font-weight: 700;
  font-size: 20px;
  color: #000000;
  margin: 10px 0px;
  text-align: center;
`;

const DoneSubtitle = styled.span`
  font-weight: 400;
  font-size: 16px;
  color: #000000;
  text-align: center;
`;

const MandatoryStar = styled.span`
  color: #ED2B00;
  font-size: 14px;
`;

const DisclaimerWrapper = styled.div`
  position: fixed;
  z-index: 375;
  top: 0px;right: 0px;bottom:0px;left:0px;
  background-color: rgba(51,51,51, 0.5);
  @media (max-width: 800px) {
    display: none;
  }
`;

const questionTypes = {
  grade: 'grade',
  dropdown: 'dropdown',
  textField: 'textField',
  radio: 'radio'
}

const surveySteps = {
  answering: 0,
  done: 1
}

const eventForceShowingSurvey = 'iac-force-showing-survey';

const standardize_color = (str) => {
  var ctx = document.createElement('canvas').getContext('2d');
  ctx.fillStyle = str;
  return ctx.fillStyle.length > 0 && ctx.fillStyle[0] === '#' ? ctx.fillStyle : rgb2hex(ctx.fillStyle);
}

const rgb2hex = (rgb) => {
  rgb = rgb.match(/^rgba?[\s+]?\([\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?/i);
  return (rgb && rgb.length === 4) ? "#" +
    ("0" + parseInt(rgb[1], 10).toString(16)).slice(-2) +
    ("0" + parseInt(rgb[2], 10).toString(16)).slice(-2) +
    ("0" + parseInt(rgb[3], 10).toString(16)).slice(-2) : '';
}

const hexToRgb = hex =>
  hex.replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i
    , (m, r, g, b) => '#' + r + r + g + g + b + b)
    .substring(1).match(/.{2}/g)
    .map(x => parseInt(x, 16));


const rgb_is_light = (color) => {
  const brightness = ((color[0] * 299) + (color[1] * 587) + (color[2] * 114)) / 1000;
  return brightness > 155;
}

class IACSurvey extends React.Component {

  static propTypes = {
    appTitle: PropTypes.string.isRequired,    // Title of the application
    surveyData: PropTypes.object,             // The survey questions
    preselectionnedAnswers: PropTypes.object, // Preselectionned answer
    getLabel: PropTypes.func.isRequired,      // Function to retrieve the label
    onOpen: PropTypes.func.isRequired,        // Callback function when the user opens the survey
    onSubmit: PropTypes.func.isRequired,      // Callback function when the user submits the data,
    color: PropTypes.string.isRequired,
    fullScreenEnabled: PropTypes.bool,
    showDisclaimer: PropTypes.bool,
    campaignId: PropTypes.string
  }

  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
      surveyAnswers: {},
      step: surveySteps.answering,
      forceShowing: false,
      textColor: rgb_is_light(hexToRgb(standardize_color(props.color || `${SECONDARY_COLOR}`))) ? 'rgb(98,100,105)' : 'white',
      showDisclaimer: props.showDisclaimer || false
    }
    this._isMounted = false;
  }

  handleForceShowing = () => {
    setTimeout(() => {
      if (this._isMounted) {
        this.setState({ forceShowing: true });
        setTimeout(() => {
          if (this._isMounted) {
            this.setState({ forceShowing: false });
          }
        }, 3000);
      }
    }, 3000);
  }

  componentDidMount = () => {
    this._isMounted = true;
    window.addEventListener(eventForceShowingSurvey, this.handleForceShowing);
  }

  componentWillUnmount = () => {
    this._isMounted = false;
    window.removeEventListener(eventForceShowingSurvey, this.handleForceShowing);
  }

  questionShouldBeDisplayed = (question, currentAnswers) => {
    let result = true;
    if (question.activeIf) {
      const previousQuestionKey = question.activeIf.previousQuestionKey;
      const previousAnswerValue = question.activeIf.previousAnswerValue;

      if (currentAnswers && currentAnswers[previousQuestionKey] && currentAnswers[previousQuestionKey].answer && currentAnswers[previousQuestionKey].answer.value !== previousAnswerValue) {
        result = false;
      }
    }

    return result;
  }


  buildDefaultAnswers = (defaultAnswers, useDefaultUserAnswers = false) => {
    let questions = [];
    if(this.props.surveyData && this.props.surveyData.questions){
      questions = this.props.surveyData.questions
    }

    questions.forEach(question => {
      if (this.questionShouldBeDisplayed(question, defaultAnswers) &&
      (defaultAnswers[question.key] == null || ((defaultAnswers[question.key].activeIf &&
        (defaultAnswers[defaultAnswers[question.key].activeIf.previousQuestionKey]) &&
        (defaultAnswers[defaultAnswers[question.key].activeIf.previousQuestionKey].answer) &&
        defaultAnswers[defaultAnswers[question.key].activeIf.previousQuestionKey].answer.value !== defaultAnswers[question.key].activeIf.previousAnswerValue)))) {
        if (question.type === questionTypes.dropdown) {
          const possibleValues = question.values || [];
          if (possibleValues.length > 0) {
            let answer = possibleValues[0];

            // If the answer should be preselected, find it inside the possible values
            if (useDefaultUserAnswers && this.props.preselectionnedAnswers && this.props.preselectionnedAnswers[question.key]) {
              const userValue = possibleValues.find(val => val.key === this.props.preselectionnedAnswers[question.key]);
              if (userValue) {
                answer = userValue;
              }
            }

            defaultAnswers[question.key] = {
              key: question.key,
              title: question.title,
              activeIf: question.activeIf,
              mandatory: question.mandatory,
              answer: {
                title: answer.title,
                value: answer.key
              }
            };
          }
        } else {
          defaultAnswers[question.key] = {
            key: question.key,
            title: question.title,
            activeIf: question.activeIf,
            mandatory: question.mandatory,
            answer: {
              value: null
            }
          };
        }
      }
    });

    return defaultAnswers;
  }

  renderQuestion = (question) => {
    return (
      <>
        <QuestionTitle>{this.props.getLabel(question.key)} {question.mandatory && <MandatoryStar>*</MandatoryStar>}</QuestionTitle>
        {question.type === questionTypes.dropdown && <SurveyDropdown
          question={question}
          surveyAnswers={this.state.surveyAnswers}
          getLabel={this.props.getLabel}
          onChange={
            (newValue) => {
              const newValues = JSON.parse(JSON.stringify(this.state.surveyAnswers));
              const questionValues = question.values || [];
              if (questionValues.length > 0) {
                const selectedValue = questionValues.find(q => q.key === newValue);
                if (selectedValue) {
                  newValues[question.key] = {
                    key: question.key,
                    title: question.title,
                    activeIf: question.activeIf,
                    mandatory: question.mandatory,
                    answer: {
                      title: selectedValue.title,
                      value: selectedValue.key
                    }
                  };
                  this.setState({ surveyAnswers: this.buildDefaultAnswers(newValues) });
                }
              }
            }
          }>
        </SurveyDropdown>}
        {question.type === questionTypes.textField && <SurveyTextField
          value={this.state.surveyAnswers[question.key] && this.state.surveyAnswers[question.key].answer && this.state.surveyAnswers[question.key].answer.value ? this.state.surveyAnswers[question.key].answer.value : null}
          getLabel={this.props.getLabel}
          onChange={(newValue) => {
            const newValues = JSON.parse(JSON.stringify(this.state.surveyAnswers));
            newValues[question.key] = {
              key: question.key,
              title: question.title,
              activeIf: question.activeIf,
              mandatory: question.mandatory,
              answer: {
                value: newValue
              }
            };

            this.setState({ surveyAnswers: this.buildDefaultAnswers(newValues) });
          }}></SurveyTextField>}
        {question.type === questionTypes.grade && <SurveyGrade
          value={this.state.surveyAnswers[question.key] && this.state.surveyAnswers[question.key].answer && this.state.surveyAnswers[question.key].answer.value ? this.state.surveyAnswers[question.key].answer.value : null}
          getLabel={this.props.getLabel}
          onChange={(newValue) => {
            const newValues = JSON.parse(JSON.stringify(this.state.surveyAnswers));
            newValues[question.key] = {
              key: question.key,
              title: question.title,
              activeIf: question.activeIf,
              mandatory: question.mandatory,
              answer: {
                value: newValue
              }
            };

            this.setState({ surveyAnswers: this.buildDefaultAnswers(newValues) });
          }}
        ></SurveyGrade>}
        {question.type === questionTypes.radio && <SurveyRadio
          question={question}
          answer={this.state.surveyAnswers[question.key].answer}
          getLabel={this.props.getLabel}
          onChange={(newValue) => {
            const newValues = JSON.parse(JSON.stringify(this.state.surveyAnswers));
            newValues[question.key].answer = newValue;
            this.setState({ surveyAnswers: this.buildDefaultAnswers(newValues) });
          }}></SurveyRadio>}
      </>
    );
  }

  renderQuestions = () => {
    let questions = [];
    if(this.props.surveyData && this.props.surveyData.questions){
      questions = this.props.surveyData.questions
    }
    return (
      <React.Fragment>
        {this.props.surveyData && this.props.surveyData.key && <SurveyTitle>{this.props.getLabel(this.props.surveyData.key)}</SurveyTitle>}
        {questions.filter(question => this.questionShouldBeDisplayed(question, this.state.surveyAnswers)).map((question, idx) => {
          return (<React.Fragment key={idx}>
            {this.renderQuestion(question)}
          </React.Fragment>)
        })}
      </React.Fragment>
    );
  }

  canSubmit = () => {
    let canSubmit = true;
    let answers = Object.values(this.state.surveyAnswers);
    for (let i = 0; i < answers.length && canSubmit; i++) {
      let answerObj = answers[i];
      if (answerObj.mandatory && (answerObj.answer.value == null || answerObj.answer.value === '')) {
        canSubmit = false;
      }
    }

    return canSubmit;
  }

  buildSubmitPayload = () => {
    let result = [];
    let questions = [];
    if(this.props.surveyData && this.props.surveyData.questions){
      questions = this.props.surveyData.questions
    }
    questions.forEach(question => {
      if (this.questionShouldBeDisplayed(question, this.state.surveyAnswers) && this.state.surveyAnswers[question.key]) {
        result.push(this.state.surveyAnswers[question.key]);
      }
    });

    return result;
  }

  openSurvey = () => {
    this.setState({ surveyAnswers: this.buildDefaultAnswers({}, true), isOpen: true, step: surveySteps.answering });
  }

  markCampaignId = () => {
    try {
      localStorage.setItem(`iac-campaign-${this.props.campaignId}`, true);
    } catch(e) {}
  }

  render = () => {
    return (
      <>
        {!this.state.isOpen && <>
          <OpenSurveyButton
            showDisclaimer={this.state.showDisclaimer}
            forceShowing={this.state.forceShowing}
            fullScreenEnabled={this.props.fullScreenEnabled}
            color={this.props.color}
            textColor={this.state.textColor}
            >
            {this.state.showDisclaimer && <SurveyDisclaimer
              color={this.props.color}
              textColor={this.state.textColor}
              getLabel={this.props.getLabel}
              onOpen={() => { this.openSurvey(); this.setState({showDisclaimer: false}); this.markCampaignId(); }}
              onDismiss={() => { this.setState({showDisclaimer: false}); this.markCampaignId(); }}
            ></SurveyDisclaimer>}

            <ButtonElements
               showDisclaimer={this.state.showDisclaimer}
               onClick={() => {
                if (this.props.onOpen) {
                  this.props.onOpen();
                }
                this.openSurvey();
            }}>

              <IconWrapper>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
                  <g>
                    <path fill="currentColor" d="M502.9,128.9H478c-11.8,0-21.3,9.6-21.3,21.3v112.6c0,61.3-24.2,110.9-71.8,147.6c-9.3,7.2-11.1,20.6-3.9,29.9   c7.2,9.3,20.6,11.1,29.9,3.9c40.4-31,88.5-87.7,88.5-181.4v-91.3h3.6c19,0,36.9,7.4,50.4,20.9c13.5,13.5,20.9,31.4,20.9,50.4v160.3   c0,11.8,9.6,21.3,21.3,21.3h218.7c20.6,0,37.4,16.8,37.4,37.4s-16.8,37.4-37.4,37.4h-67.2c-11.8,0-21.3,9.6-21.3,21.3   s9.6,21.3,21.3,21.3h67.2c20.6,0,37.4,16.8,37.4,37.4S834.9,617,814.3,617H747c-11.8,0-21.3,9.6-21.3,21.3s9.6,21.3,21.3,21.3h49.8   c20.6,0,37.4,16.8,37.4,37.4s-16.8,37.4-37.4,37.4h-51.5c-11.8,0-21.3,9.6-21.3,21.3s9.6,21.3,21.3,21.3   c20.6,0,37.4,16.8,37.4,37.4s-16.8,37.4-37.4,37.4H400.3c-33.6,0-57.1-23.9-57.1-58.1c0-11.8-9.6-21.3-21.3-21.3   s-21.3,9.6-21.3,21.3v6.8c0,12.5-5.9,25.4-16.3,35.6c-10.1,9.9-23.2,15.8-35,15.8h-76.3V488.2h73c30.1,0,54.6,24.5,54.6,54.6v111.9   c0,11.8,9.6,21.3,21.3,21.3s21.3-9.6,21.3-21.3V542.8c0-53.7-43.7-97.3-97.3-97.3h-94.3c-11.8,0-21.3,9.6-21.3,21.3v406.6   c0,11.8,9.6,21.3,21.3,21.3h97.7c28.2,0,55-14.7,72.7-36.6c2.4,3,5,5.9,7.7,8.6c18.3,18,43.4,28,70.7,28h345.1   c44.2,0,80.1-35.9,80.1-80.1c0-14.4-3.8-27.9-10.4-39.5c35.4-8.3,61.9-40.1,61.9-78c0-19.3-6.9-37.1-18.3-50.9   c21.5-14.4,35.7-38.9,35.7-66.7c0-23.2-9.9-44.1-25.7-58.8c15.8-14.6,25.7-35.6,25.7-58.8c0-44.2-35.9-80.1-80.1-80.1H616.9v-139   C616.9,180,565.7,128.9,502.9,128.9z" />
                  </g>
                </svg>
              </IconWrapper>
              <OpenSurveyLabel>{this.props.getLabel('embedded_survey.feedback')}</OpenSurveyLabel>
            </ButtonElements>

          </OpenSurveyButton>
          {this.state.showDisclaimer && <DisclaimerWrapper></DisclaimerWrapper>}
        </>}
        {this.state.isOpen && <SurveyBackground onClick={() => this.setState({ isOpen: false })}>
          <SurveyWrapper onClick={(event) => {
            event.stopPropagation(); // Prevent parent onClick to be triggered
          }}>
            <SurveyHeader color={this.props.color}
              textColor={this.state.textColor}>
              <AppTitle>{this.props.appTitle}</AppTitle>
              <CloseIconWrapper onClick={() => this.setState({ isOpen: false })}>
                <IconWrapper>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
                    <path fill="currentColor" d="M512.1,128.1c-212,0-384.1,172-384.1,383.9c0,212.2,172.1,383.9,384.1,383.9C724.2,895.9,896,724.2,896,512  C896,300.1,724.2,128.1,512.1,128.1z M674.6,674.7c-13.5,13.5-35.3,13.5-48.8,0c0,0,0,0,0,0L511.9,560.9L398.2,674.7  c-13.5,13.5-35.4,13.5-48.9,0c-13.4-13.5-13.4-35.4,0-48.9L463.1,512L349.3,398.2c-13.3-13.7-12.9-35.6,0.8-48.9  c13.4-13,34.7-13,48.1,0l113.7,113.8l113.8-113.8c13.5-13.5,35.4-13.5,48.9,0c13.5,13.5,13.5,35.4,0,48.9L560.8,512l113.8,113.8  c13.5,13.4,13.6,35.3,0.2,48.8C674.7,674.6,674.7,674.6,674.6,674.7z" />
                  </svg>
                </IconWrapper>
              </CloseIconWrapper>
            </SurveyHeader>
            <SurveyContent>
              {this.state.step === surveySteps.answering && <>
                <Scrollbars>
                  <ScrollablePadding>
                    {this.renderQuestions()}
                  </ScrollablePadding>
                </Scrollbars>
                <ButtonsWrapper>
                  <SurveyButton text={this.props.getLabel('embedded_survey.cancel_generic')} color={this.props.color} outline={true} callback={() => this.setState({ isOpen: false })}></SurveyButton>
                  <ButtonWrapper>
                    <SurveyButton text={this.props.getLabel('embedded_survey.send_my_feedback_generic')} color={this.props.color} textColor={this.state.textColor} disabled={!this.canSubmit()} callback={() => {
                        this.props.onSubmit(this.buildSubmitPayload());
                        this.setState({ step: surveySteps.done })
                        setTimeout(() => {
                          if (this._isMounted) {
                            this.setState({ isOpen: false });
                          }
                        }, 5000);
                      }}></SurveyButton>
                  </ButtonWrapper>
                </ButtonsWrapper>
              </>}
              {this.state.step === surveySteps.done && <DoneWrapper>
                <FinishedIconWrapper color={this.props.color} textColor={this.state.textColor}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
                    <path fill="currentColor" d="M512 128c212 0 384 172 384 384S724 896 512 896 128 724 128 512s172-384 384-384zm189 292c12-12 12-31 0-44-12-12-32-12-45 0L451 583l-83-85c-13-13-33-13-45 0-13 12-13 33 0 45l105 106c6 6 14 10 23 10 8 0 16-4 23-10z" />
                  </svg>
                </FinishedIconWrapper>
                <DoneTitle>{this.props.getLabel('embedded_survey.thank_you_generic')}</DoneTitle>
                <DoneSubtitle>{this.props.getLabel('embedded_survey.your_feedback_is_valuable_generic')}</DoneSubtitle>
              </DoneWrapper>}
            </SurveyContent>
          </SurveyWrapper>
        </SurveyBackground>
        }
      </>
    );
  }
}

export default IACSurvey;
